import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useContext,
} from "react";
import useLocalStorage from "use-local-storage";

export const ThemeContext = createContext(undefined);

export const ThemeProvider = ({ children }) => {
  // Safe default for SSR
  const prefersDark =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  // Persisted state
  const [isDark, setIsDark] = useLocalStorage("isDark", prefersDark);
  // Default a palette to avoid "light-undefined"
  const [colorTheme, setColorTheme] = useLocalStorage(
    "colorTheme",
    "grayscale"
  );
  const [customHue, setCustomHue] = useLocalStorage("customHue", 240);

  // Compose a full theme name (mode + palette), or "custom"
  const getThemeName = useCallback(() => {
    const mode = isDark ? "dark" : "light";
    if (colorTheme === "custom") return "custom";
    return `${mode}-${colorTheme}`;
  }, [isDark, colorTheme]);

  // Apply theme to <html> via data-theme and custom props
  useEffect(() => {
    if (typeof document === "undefined") return;

    const themeName = getThemeName();
    const root = document.documentElement;

    root.setAttribute("data-theme", themeName);

    if (colorTheme === "custom") {
      root.style.setProperty("--base-hue", String(customHue));
      root.style.setProperty("--is-dark-mode", isDark ? "1" : "0");
    } else {
      // Clean up custom-only props if switching away
      root.style.removeProperty("--base-hue");
      root.style.removeProperty("--is-dark-mode");
    }
  }, [isDark, colorTheme, customHue, getThemeName]);

  // Backward compatibility with older setTheme usage
  const setTheme = useCallback(
    (newTheme) => {
      if (!newTheme) return;

      if (newTheme === "light") {
        setIsDark(false);
        setColorTheme("grayscale");
        return;
      }
      if (newTheme === "dark") {
        setIsDark(true);
        setColorTheme("grayscale");
        return;
      }
      if (newTheme === "custom") {
        setColorTheme("custom");
        return;
      }

      // Handle compound names like "light-mono-red" or "dark-blue"
      const parts = String(newTheme).split("-");
      if (parts.length >= 2 && (parts[0] === "light" || parts[0] === "dark")) {
        const mode = parts[0];
        const base = parts.slice(1).join("-");
        setIsDark(mode === "dark");
        setColorTheme(base || "grayscale");
      } else {
        console.warn("Unknown theme format:", newTheme);
      }
    },
    [setIsDark, setColorTheme]
  );

  const value = useMemo(
    () => ({
      // public state
      isDark,
      setIsDark,
      colorTheme,
      setColorTheme,
      customHue,
      setCustomHue,

      // derived
      currentTheme: getThemeName(),

      // legacy names
      theme: getThemeName(),
      setTheme,
    }),
    [
      isDark,
      colorTheme,
      customHue,
      getThemeName,
      setTheme,
      setIsDark,
      setColorTheme,
      setCustomHue,
    ]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Convenience hook
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
}

export default ThemeProvider;
